import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaSearch, FaTimes, FaClock, FaArrowRight, 
  FaTerminal, FaFire, FaLightbulb, FaBrain, FaBoxOpen, FaBolt
} from 'react-icons/fa';
import { posts } from '../data/posts';

const badgeConfig = {
  amber: {
    badge: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
    icon: <FaFire className="text-amber-500" size={11} />,
    glow: 'from-amber-500/15 via-zinc-900/10 to-transparent'
  },
  blue: {
    badge: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
    icon: <FaLightbulb className="text-blue-500" size={11} />,
    glow: 'from-blue-500/15 via-zinc-900/10 to-transparent'
  },
  purple: {
    badge: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20',
    icon: <FaBrain className="text-purple-500" size={11} />,
    glow: 'from-purple-500/15 via-zinc-900/10 to-transparent'
  },
  emerald: {
    badge: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
    icon: <FaBoxOpen className="text-emerald-500" size={11} />,
    glow: 'from-emerald-500/15 via-zinc-900/10 to-transparent'
  },
  default: {
    badge: 'bg-zinc-500/10 text-zinc-600 dark:text-zinc-400 border-zinc-500/20',
    icon: <FaBolt className="text-zinc-500" size={11} />,
    glow: 'from-zinc-500/15 via-zinc-900/10 to-transparent'
  }
};

const Blog = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [sortBy, setSortBy] = useState('newest');

  const categories = useMemo(() => {
    const cats = [...new Set(posts.map(p => p.category))];
    return ['All', ...cats];
  }, []);

  const categoryCounts = useMemo(() => {
    const counts = { All: posts.length };
    posts.forEach(p => {
      counts[p.category] = (counts[p.category] || 0) + 1;
    });
    return counts;
  }, []);

  const filteredPosts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let working = posts;

    if (selectedCategory !== 'All') {
      working = working.filter(p => p.category === selectedCategory);
    }

    if (q) {
      working = working.filter(p => 
        p.title.toLowerCase().includes(q) ||
        p.excerpt.toLowerCase().includes(q) ||
        p.tags.some(t => t.toLowerCase().includes(q))
      );
    }

    return working.slice().sort((a, b) => {
      if (sortBy === 'newest') {
        return new Date(b.date) - new Date(a.date);
      } else if (sortBy === 'oldest') {
        return new Date(a.date) - new Date(b.date);
      } else if (sortBy === 'title') {
        return a.title.localeCompare(b.title);
      }
      return 0;
    });
  }, [searchQuery, selectedCategory, sortBy]);

  return (
    <div className="min-h-screen pt-28 pb-20 px-4 sm:px-6 max-w-7xl mx-auto space-y-12 select-text">
      
      {/* ========================================================= */}
      {/* 1. HEADER & LAB INTRO */}
      {/* ========================================================= */}
      <div className="space-y-4 text-center max-w-3xl mx-auto">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-mono font-medium bg-primary/10 text-primary dark:text-dark-primary border border-primary/20 backdrop-blur-md">
          <FaTerminal className="w-3.5 h-3.5" />
          <span>Technical Lab & Engineering Journal</span>
        </div>
        
        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-zinc-900 dark:text-white">
          Engineering Notes & Benchmarks
        </h1>
        <p className="text-base sm:text-lg text-zinc-600 dark:text-zinc-400 leading-relaxed">
          Deep dives into distributed consensus, low-level concurrency, Linux kernel tuning, and neural architectures.
        </p>
      </div>

      {/* ========================================================= */}
      {/* 2. SEARCH & FILTER BAR */}
      {/* ========================================================= */}
      <div className="space-y-6">
        {/* Search Bar */}
        <div className="relative flex items-center">
          <FaSearch className="absolute left-5 text-zinc-400 pointer-events-none" size={16} />
          <input
            type="text"
            placeholder="Search benchmarks, architecture notes, or tags (e.g., Go, Rust, PyTorch)..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-12 py-4 rounded-full bg-white/80 dark:bg-zinc-900/80 border border-zinc-200/80 dark:border-zinc-800/80 text-zinc-900 dark:text-white placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary/40 dark:focus:ring-dark-primary/40 backdrop-blur-md shadow-sm text-sm sm:text-base transition-all"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-5 p-1 rounded-full text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200"
              aria-label="Clear search"
            >
              <FaTimes size={14} />
            </button>
          )}
        </div>

        {/* Filter Chips & Sort Dropdown */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center flex-wrap gap-2">
            {categories.map(cat => {
              const isSelected = selectedCategory === cat;
              return (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-all duration-200 border ${
                    isSelected
                      ? 'bg-zinc-900 text-white dark:bg-white dark:text-zinc-900 border-zinc-900 dark:border-white shadow-sm'
                      : 'bg-white/60 dark:bg-zinc-900/60 text-zinc-600 dark:text-zinc-400 border-zinc-200/80 dark:border-zinc-800/80 hover:bg-zinc-100 dark:hover:bg-zinc-800'
                  }`}
                >
                  <span>{cat}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-mono ${
                    isSelected ? 'bg-white/20 text-white dark:bg-zinc-900/20 dark:text-zinc-900' : 'bg-zinc-200/60 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400'
                  }`}>
                    {categoryCounts[cat] || 0}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-3 self-end sm:self-auto">
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              className="text-xs sm:text-sm font-medium px-4 py-2 rounded-full bg-white/70 dark:bg-zinc-900/70 border border-zinc-200/80 dark:border-zinc-800/80 text-zinc-700 dark:text-zinc-300 focus:outline-none focus:ring-2 focus:ring-primary/40 backdrop-blur-md cursor-pointer"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="title">Title (A → Z)</option>
            </select>
          </div>
        </div>
      </div>

      {/* ========================================================= */}
      {/* 3. BENTO GRID LAYOUT */}
      {/* ========================================================= */}
      {filteredPosts.length === 0 ? (
        <div className="text-center py-20 bg-white/40 dark:bg-zinc-900/40 rounded-3xl border border-zinc-200/60 dark:border-zinc-800/60">
          <p className="text-zinc-500 text-base">No articles found matching your query.</p>
          <button
            onClick={() => { setSearchQuery(''); setSelectedCategory('All'); }}
            className="mt-4 text-xs font-semibold text-primary dark:text-dark-primary hover:underline"
          >
            Reset Filters
          </button>
        </div>
      ) : (
        <motion.div layout className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6">
          <AnimatePresence>
            {filteredPosts.map((post) => {
              const cfg = badgeConfig[post.badgeColor] || badgeConfig.default;
              const isHero = post.bentoSize === 'hero';
              const isWide = post.bentoSize === 'wide';
              const isMini = post.bentoSize === 'mini';

              return (
                <motion.div
                  key={post.slug}
                  layout
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  whileHover={{ y: -6 }}
                  transition={{ duration: 0.25 }}
                  className={`group relative rounded-3xl bg-white/80 dark:bg-zinc-900/80 border border-zinc-200/80 dark:border-zinc-800/80 backdrop-blur-md p-6 sm:p-8 flex flex-col justify-between hover:shadow-2xl hover:border-zinc-400 dark:hover:border-zinc-600 transition-all duration-300 ${
                    isHero || isWide ? 'md:col-span-2' : 'md:col-span-1'
                  }`}
                >
                  {/* Subtle Background Glow for Featured/Hero tiles */}
                  {isHero && (
                    <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 via-transparent to-primary/5 rounded-3xl pointer-events-none" />
                  )}

                  <div className="space-y-4 relative z-10">
                    {/* Header: Badge & Date */}
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-mono font-bold px-3 py-1 rounded-full border ${cfg.badge}`}>
                        {cfg.icon}
                        <span>{post.badge}</span>
                      </span>

                      <div className="flex items-center gap-2 text-xs font-mono text-zinc-400 dark:text-zinc-500">
                        <span>{post.date}</span>
                        <span>•</span>
                        <span className="flex items-center gap-1">
                          <FaClock size={10} />
                          <span>{post.readTime}</span>
                        </span>
                      </div>
                    </div>

                    {/* Title */}
                    <Link to={`/blog/${post.slug}`} className="block">
                      <h2 className={`font-extrabold text-zinc-900 dark:text-white group-hover:text-primary dark:group-hover:text-dark-primary transition-colors leading-tight ${
                        isHero ? 'text-2xl sm:text-3xl' : isMini ? 'text-lg' : 'text-xl'
                      }`}>
                        {post.title}
                      </h2>
                    </Link>

                    {/* Excerpt */}
                    <p className={`text-zinc-600 dark:text-zinc-300 leading-relaxed ${
                      isMini ? 'text-xs line-clamp-3' : 'text-sm'
                    }`}>
                      {post.excerpt}
                    </p>
                  </div>

                  {/* Footer: Tags & Read Link */}
                  <div className="pt-6 mt-6 border-t border-zinc-200/60 dark:border-zinc-800/60 flex items-center justify-between gap-4 relative z-10">
                    <div className="flex flex-wrap gap-1.5">
                      {post.tags.slice(0, isMini ? 2 : 4).map(tag => (
                        <span
                          key={tag}
                          className="text-[11px] font-mono px-2.5 py-0.5 rounded-md bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 border border-zinc-200/40 dark:border-zinc-700/40"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>

                    <Link
                      to={`/blog/${post.slug}`}
                      className="inline-flex items-center gap-1.5 text-xs font-bold text-primary dark:text-dark-primary group-hover:translate-x-1 transition-transform shrink-0"
                    >
                      <span>Read</span>
                      <FaArrowRight size={11} />
                    </Link>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </motion.div>
      )}

    </div>
  );
};

export default Blog;
